<template>
  <div class="gantt-main">
    <div class="gantt-scroll-container" ref="ganttMain" @scroll="scrolling">
      <div class="top-left-container" ref="top-left-container" :style="topLeftContainerStyling"></div>
      <div
        class="left-container"
        :style="{
          width: `${leftContainerWidth}px`,
          minWidth: `${leftContainerWidth}px`,
          maxWidth: `${leftContainerWidth}px`,
        }"
        ref="left-container"
      ></div>
      <div
        class="top-container"
        ref="top-container"
        :style="{
          width: `calc(100% - ${leftContainerWidth}px)`,
        }"
      >
        <div class="top-row" ref="month-row"></div>
        <div class="top-row" ref="date-row"></div>
        <div class="top-row time-row" ref="time-row"></div>
      </div>
      <div class="task-container" :style="taskContainerStyling" ref="task-container"> </div>

      <div v-show="showNowMarker" class="now-marker" ref="now-marker">
        <div class="now-marker-text">Now</div>
      </div>
    </div>

    <div v-if="showZoomControls" class="gantt-zoom-controls" role="group" aria-label="Timeline zoom">
      <button
        type="button"
        :disabled="!canZoomOut()"
        :aria-label="`Zoom out to ${zoomOutLabel()}`"
        :title="`Zoom out to ${zoomOutLabel()}`"
        @click="zoomOut"
      >
        −
      </button>
      <button
        type="button"
        :disabled="!canZoomIn()"
        :aria-label="`Zoom in to ${zoomInLabel()}`"
        :title="`Zoom in to ${zoomInLabel()}`"
        @click="zoomIn"
      >
        +
      </button>
    </div>
    <button
      v-show="showGoToNow"
      type="button"
      class="to-now-button"
      :style="toNowButtonStyling"
      aria-label="Go to the current time"
      @click="goToNow($event)"
    >
      <span v-if="toNowButtonStyling['justifyContent'] == 'flex-start'">◁</span>
      <span class="mid-text">Go To Now</span>
      <span v-if="toNowButtonStyling['justifyContent'] == 'flex-end'">▷</span>
    </button>
  </div>
</template>

<script>
/* eslint-disable */
function getTaskEndTimestamp(taskItem) {
  const start = new Date(taskItem && taskItem['cbstart']).getTime();
  if (isNaN(start)) {
    return NaN;
  }

  const explicitEnd = new Date(taskItem && taskItem['cbend']).getTime();
  if (!isNaN(explicitEnd) && explicitEnd > start) {
    return explicitEnd;
  }

  const duration = parseFloat(taskItem && taskItem['cbduration']);
  return start + (isNaN(duration) ? 0 : Math.max(0, duration) * 60 * 60 * 1000);
}

const DAILY_INTERVAL = 1440;
const HALF_DAY_INTERVAL = 720;
const SIX_HOUR_INTERVAL = 360;
const HALF_DAY_COLUMNS_PER_DAY = 2;
const SIX_HOUR_COLUMNS_PER_DAY = 4;
const MINIMUM_VISIBLE_DAY_COLUMNS = 7;

// Daily is deliberately the widest supported zoom level.
const ganttZoomLevels = [
  { interval: DAILY_INTERVAL, label: '1 day' },
  { interval: HALF_DAY_INTERVAL, label: 'Half day' },
  { interval: SIX_HOUR_INTERVAL, label: '6 hours' },
  { interval: 60, label: 'Hourly' },
  { interval: 30, label: '30 min' },
  { interval: 15, label: '15 min' },
];

export default {
  components: {},
  data: function () {
    return {
      start: new Date(new Date().getTime() - 60 * 60 * 1000),
      end: new Date(new Date().getTime() + 60 * 60 * 1000),
      // start: new Date('2021-10-11 10:05'),
      // end: new Date('2021-10-11 10:30'),

      showNowMarker: true,
      nowMarkerInterval: null,

      totalCol: 0,
      timeWidth: 0,
      viewportRestoreFrame: 0,
      queuedViewportState: null,

      mainGantt: null,

      leftContainerWidth: 240,

      topLeftContainerStyling: {
        left: 0,
        top: 0,
        width: 0,
        minWidth: 0,
        maxWidth: 0,
      },
      taskContainerStyling: {
        top: '60px',
        left: '0px',
        width: '100%',
      },
      toNowButtonStyling: {
        left: 0,
        justifyContent: 'flex-start',
      },
      showGoToNow: false,
      isNavigatingToNow: false,
      goToNowScrollEndTimer: 0,
      resizing: false,
      resizeStartWidth: 0,
      resizeOffset: 0,
      resizeStartLeft: 0,
      pendingResizeWidth: 0,
      resizeFrame: 0,
      layoutResizeObserver: null,
      layoutResizeFrame: 0,
      layoutRebuildTimer: 0,
      layoutResizeSuspendedUntil: 0,
      externalLayoutResizePaused: false,
      lastObservedTimelineWidth: 0,
      verticalLinePosition: 0,
      taskContainerHandlers: null,
      hasAutoScrolledToNow: false,

      dayList: ['Sun', 'Mon', 'Tues', 'Wed', 'Thu', 'Fri', 'Sat'],
      monthList: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ],
    };
  },
  props: {
    interval: { type: Number, default: 30 }, // Interval in minute for each column
    items: {
      type: Array,
      default: function () {
        return [];
      },
    }, // The items that goes into the gantt chart (data from API MUST match the data for vehicle schedule page)
    rangeStart: { type: [String, Date], default: null },
    rangeEnd: { type: [String, Date], default: null },
    selectedTaskKey: { type: String, default: '' },
    selectedRowKey: { type: String, default: '' },
    showCategoryRows: { type: Boolean, default: true },
    useSourceTaskBorder: { type: Boolean, default: true },
    showZoomControls: { type: Boolean, default: false },
    titleMaxWidth: {
      type: Array,
      default: function () {
        return [];
      },
    }, // Max width of the left column. Eg: [ '', '150px' ] - No max width for first column and 150px max width for second column
    timeZoneWidth: { type: Number, default: 0 },
    // Public events
    // taskClicked - fired when any task is clicked, return the object of the task
    // taskDoubleClicked - fired when a non-category task is double-clicked
    // rowClicked - fired when any row is clicked (left side), return all the task for the row
    // intervalChanged - fired by native zoom controls with the selected interval in minutes
  },
  computed: {
    effectiveInterval: function () {
      const requestedInterval = Number(this.interval);
      if (!isFinite(requestedInterval) || requestedInterval <= 0) {
        return 30;
      }

      // Never render an interval wider than Daily, even while the
      // parent is still replacing an older unsupported interval value.
      return Math.min(DAILY_INTERVAL, requestedInterval);
    },
  },
  methods: {
    generateGantt: function (preserveLeftColumns) {
      // Remove all task
      const taskContainer = this.$refs['task-container'];
      Array.from(taskContainer.children).forEach((d) => taskContainer.removeChild(d));
      Array.from(this.$refs['ganttMain'].children)
        .filter((d) => d.classList && d.classList.contains('timeline-grid-overlay'))
        .forEach((d) => this.$refs['ganttMain'].removeChild(d));
      // Remove all time
      Array.from(this.$refs['time-row'].children).forEach((t) => this.$refs['time-row'].removeChild(t));
      // Remove all dates
      Array.from(this.$refs['date-row'].children).forEach((d) => this.$refs['date-row'].removeChild(d));
      // Remove all months
      Array.from(this.$refs['month-row'].children).forEach((d) => this.$refs['month-row'].removeChild(d));
      if (!preserveLeftColumns) {
        // Remove all title
        Array.from(this.$refs['top-left-container'].children).forEach((d) =>
          this.$refs['top-left-container'].removeChild(d),
        );
        // Remove all left column
        Array.from(this.$refs['left-container'].children).forEach((d) => this.$refs['left-container'].removeChild(d));
      }

      // Measure the usable timeline viewport only when the Gantt is actually
      // generated. Right-panel opening/resizing does not regenerate it, so
      // these column widths remain fixed and horizontal overflow takes over.
      // clientWidth excludes the permanently reserved vertical scrollbar.
      this.$refs['time-row'].style.width = '';
      const availableTimelineWidth = this.getTimelineViewportWidth();
      let colWidth = this.totalCol > 0 ? availableTimelineWidth / this.totalCol : availableTimelineWidth;
      let timelineWidth = 0;
      if (this.effectiveInterval === DAILY_INTERVAL) {
        // Each calendar day is one timeline cell. Keep at least seven days
        // visible before horizontal overflow takes over.
        const minimumDayColumnWidth = availableTimelineWidth / MINIMUM_VISIBLE_DAY_COLUMNS;

        colWidth = Math.max(minimumDayColumnWidth, colWidth);
        timelineWidth = colWidth * this.totalCol;
      } else if (this.effectiveInterval === HALF_DAY_INTERVAL) {
        // A date column is one complete day, made from two Half Day cells.
        // Keep each Half Day cell the same width as a Daily cell.
        const minimumDayColumnWidth = availableTimelineWidth / MINIMUM_VISIBLE_DAY_COLUMNS;

        colWidth = Math.max(minimumDayColumnWidth, colWidth);
        timelineWidth = colWidth * this.totalCol;
      } else if (this.effectiveInterval === SIX_HOUR_INTERVAL) {
        // Four six-hour cells make one calendar day. Keep each cell the same
        // width as a Daily and Half Day cell so the intermediate zoom remains readable.
        const minimumDayColumnWidth = availableTimelineWidth / MINIMUM_VISIBLE_DAY_COLUMNS;

        colWidth = Math.max(minimumDayColumnWidth, colWidth);
        timelineWidth = colWidth * this.totalCol;
      } else {
        const minimumColumnWidth = this.effectiveInterval === 60 ? 50 : 35;
        colWidth = Math.max(minimumColumnWidth, Math.ceil(colWidth));
        timelineWidth = Math.max(colWidth * this.totalCol, availableTimelineWidth);
      }
      const renderedTimelineWidth = Math.max(timelineWidth, availableTimelineWidth);
      const hasTrailingTimelineSpace = renderedTimelineWidth > timelineWidth;
      const trailingTimelineWidth = renderedTimelineWidth - timelineWidth;
      this.timeWidth = colWidth;
      const gridColor = '#eef0f2';
      const hourGridColor = '#d9dde2';
      const hourColumnCount =
        this.effectiveInterval === DAILY_INTERVAL ?
          1
        : this.effectiveInterval === HALF_DAY_INTERVAL ?
          HALF_DAY_COLUMNS_PER_DAY
        : this.effectiveInterval === SIX_HOUR_INTERVAL ?
          SIX_HOUR_COLUMNS_PER_DAY
        : Math.max(1, Math.round(60 / this.effectiveInterval));
      const timelineGridOverlay = this.createDiv(null, ['timeline-grid-overlay']);
      timelineGridOverlay.setAttribute('aria-hidden', 'true');
      timelineGridOverlay.style.top = '60px';
      timelineGridOverlay.style.left = `${this.leftContainerWidth}px`;
      timelineGridOverlay.style.width = `${renderedTimelineWidth}px`;
      timelineGridOverlay.style.height = '0px';
      timelineGridOverlay.style.gridTemplateColumns =
        hasTrailingTimelineSpace ?
          `repeat(${this.totalCol}, ${colWidth}px) minmax(0, 1fr)`
        : `repeat(${this.totalCol}, ${colWidth}px)`;
      timelineGridOverlay.style.setProperty('--gantt-grid-color', gridColor);
      timelineGridOverlay.style.setProperty('--gantt-hour-grid-color', hourGridColor);

      for (let i = 0; i < this.totalCol; i++) {
        const classNames = ['timeline-grid-column'];
        if (i < this.totalCol - 1 && (i + 1) % hourColumnCount === 0) {
          classNames.push('hour-boundary');
        }
        if (i === this.totalCol - 1 && !hasTrailingTimelineSpace) {
          classNames.push('last-column');
        }
        timelineGridOverlay.appendChild(this.createDiv(null, classNames));
      }
      if (hasTrailingTimelineSpace) {
        timelineGridOverlay.appendChild(
          this.createDiv(null, ['timeline-grid-column', 'timeline-grid-trailing-column', 'last-column']),
        );
      }
      this.$refs['ganttMain'].appendChild(timelineGridOverlay);
      this.$refs['time-row'].style.width = `${renderedTimelineWidth}px`;

      let time = new Date(this.start);
      let date = time.getDate();
      let dayChange = [];
      let month = time.getMonth();
      let monthChange = [];

      for (let i = 0; i < this.totalCol; i++) {
        const nextTime = new Date(time.getTime() + this.effectiveInterval * 60 * 1000);
        const timeLabel = this.effectiveInterval === DAILY_INTERVAL ? '' : `${time.formatString('HHmm')}`;
        this.$refs['time-row'].appendChild(
          this.createDiv(timeLabel, ['row-content'], {
            width: `${colWidth}px`,
            minWidth: `${colWidth}px`,
            maxWidth: `${colWidth}px`,
            flex: `0 0 ${colWidth}px`,
            borderRight:
              i == this.totalCol - 1 && !hasTrailingTimelineSpace ?
                'none'
              : `1px solid ${(i + 1) % hourColumnCount === 0 ? hourGridColor : gridColor}`,
            color: 'gray',
          }),
        );

        time = nextTime;
        if (time.getDate() != date) {
          dayChange.push({ index: i, date: new Date(time.getTime() - 60000) });
        }
        if (time.getMonth() != month) {
          monthChange.push({ index: i, date: new Date(time.getTime() - 60000) });
        }
        date = time.getDate();
        month = time.getMonth();
      }

      if (dayChange.length == 0) {
        dayChange.push({ index: this.totalCol - 1, date: new Date(time.getTime() - 60000) });
      } else if (dayChange[dayChange.length - 1]['index'] != this.totalCol - 1) {
        dayChange.push({ index: this.totalCol - 1, date: new Date(time.getTime() - 60000) });
      }

      if (monthChange.length == 0) {
        monthChange.push({ index: this.totalCol - 1, date: new Date(time.getTime() - 60000) });
      } else if (monthChange[monthChange.length - 1]['index'] != this.totalCol - 1) {
        monthChange.push({ index: this.totalCol - 1, date: new Date(time.getTime() - 60000) });
      }

      dayChange.forEach((d, i) => {
        let width = 0;
        if (i == 0) {
          width = colWidth * (d['index'] + 1);
        } else {
          let length = d['index'] - 1 - dayChange[i - 1]['index'];
          width = colWidth * (length + 1);
        }

        let text = `${d['date'].getDate()}, ${this.dayList[d['date'].getDay()]}`;
        let classes = ['row-content'];
        if (
          `${d['date'].getFullYear()}-${d['date'].getMonth() + 1}-${d['date'].getDate()}` ==
          `${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`
        ) {
          classes.push('today-row-date');
        }
        this.$refs['date-row'].appendChild(
          this.createDiv(text, classes, {
            width: `${width}px`,
            borderRight: i == dayChange.length - 1 && !hasTrailingTimelineSpace ? 'none' : '',
          }),
        );
      });

      monthChange.forEach((m, i) => {
        let width = 0;
        if (i == 0) {
          width = colWidth * (m['index'] + 1);
        } else {
          let length = m['index'] - 1 - monthChange[i - 1]['index'];
          width = colWidth * (length + 1);
        }

        let text = `${this.monthList[m['date'].getMonth()]}, ${m['date'].getFullYear()}`;
        this.$refs['month-row'].appendChild(
          this.createDiv(text, ['row-content'], {
            width: `${width}px`,
            borderRight: i == monthChange.length - 1 && !hasTrailingTimelineSpace ? 'none' : '',
          }),
        );
      });

      if (hasTrailingTimelineSpace) {
        const trailingCellStyle = { width: `${trailingTimelineWidth}px` };
        this.$refs['time-row'].appendChild(
          this.createDiv('', ['row-content', 'timeline-trailing-cell'], trailingCellStyle),
        );
        this.$refs['date-row'].appendChild(
          this.createDiv('', ['row-content', 'timeline-trailing-cell'], trailingCellStyle),
        );
        this.$refs['month-row'].appendChild(
          this.createDiv('', ['row-content', 'timeline-trailing-cell'], trailingCellStyle),
        );
      }

      this.taskContainerStyling['left'] = `${this.leftContainerWidth}px`;
      this.taskContainerStyling['width'] = `${renderedTimelineWidth}px`;

      if (!preserveLeftColumns) {
        // Add resizer
        let resizer = this.createDiv(null, ['left-resizer'], null);
        resizer.addEventListener('mousedown', (e) => {
          let resizeStartLeft = e.target.getBoundingClientRect().left;
          this.resizeStartWidth = this.leftContainerWidth;
          this.resizeStartLeft = resizeStartLeft;
          this.resizeOffset = e.pageX - resizeStartLeft;
          this.pendingResizeWidth = this.leftContainerWidth;
          this.resizing = true;
        });
        this.$refs['left-container'].appendChild(resizer);
        // Add resizer
      }

      if (this.items.length > 0) {
        this.populateItems(preserveLeftColumns);
      }

      if (this.nowMarkerInterval) {
        window.clearInterval(this.nowMarkerInterval);
      }

      this.placeNowMarker();
      if (this.showNowMarker) {
        let now = new Date();
        let delay = 60000 - (now.getSeconds() * 1000 + now.getMilliseconds());
        setTimeout(() => {
          this.placeNowMarker();
          this.nowMarkerInterval = setInterval(this.placeNowMarker, 60000);
        }, delay);
      }
    },
    populateItems: function (preserveLeftColumns) {
      const parentGroups = [];
      const parentLookup = Object.create(null);
      this.items.forEach((item) => {
        const parentKey = String(item['pid'] || '');
        if (!parentLookup[parentKey]) {
          parentLookup[parentKey] = { key: parentKey, items: [] };
          parentGroups.push(parentLookup[parentKey]);
        }
        parentLookup[parentKey].items.push(item);
      });
      const rowHeightGroups = [];

      let colTitles = [];
      if (this.items[0]['col1title']) {
        colTitles.push('col1');
      }
      if (this.items[0]['col2title']) {
        colTitles.push('col2');
      }
      if (this.items[0]['col3title']) {
        colTitles.push('col3');
      }

      if (!preserveLeftColumns) {
        // Left title
        colTitles.forEach((c, i) => {
          let style = null;
          let maxWidth = this.titleMaxWidth[i];
          if (maxWidth) {
            style = { maxWidth: maxWidth };
          }
          this.$refs['top-left-container'].appendChild(
            this.createDiv(this.items[0][`${c}title`], ['left-column-text'], style),
          );
        });
      }
      const preservedLeftRows =
        preserveLeftColumns ?
          Array.from(this.$refs['left-container'].children).filter((row) => !row.classList.contains('left-resizer'))
        : null;
      let preservedRowIndex = 0;

      parentGroups.forEach((parentGroup) => {
        const p = parentGroup.key;
        const allChild = parentGroup.items;
        let parentTimeLine = allChild.map((pi) => {
          let start = new Date(pi['cbstart']).getTime();
          return { start: new Date(start), end: new Date(getTaskEndTimestamp(pi)) };
        });
        let pstart = Math.min.apply(
          null,
          parentTimeLine.map((t) => t['start']),
        );
        let pend = Math.max.apply(
          null,
          parentTimeLine.map((t) => t['end']),
        );

        const childGroups = [];
        const childLookup = Object.create(null);
        allChild.forEach((child) => {
          const childKey = String(child['cid'] || '');
          if (!childLookup[childKey]) {
            childLookup[childKey] = { key: childKey, items: [] };
            childGroups.push(childLookup[childKey]);
          }
          childLookup[childKey].items.push(child);
        });
        let rowIndex = 0;

        // Left side column (Parent)
        let leftColumn = allChild[0];
        let taskLeftColumns = this.createDiv(null, ['task-row'], null);

        // For close child button
        // let closeImg = this.createImg('https://dsmlib.logistics.systems/Icons/icons8-reduce-50.png', null, {
        //   height: '15px',
        //   width: '15px',
        //   cursor: 'pointer',
        //   marginLeft: '5px'
        // });
        // closeImg.setAttribute('indexes', JSON.stringify({ show: true, startIndex: this.$refs['left-container'].children.length, endIndex: this.$refs['left-container'].children.length + uniqueChild.length }));
        // closeImg.addEventListener('click', (e) => {
        //   let img = e.target;
        //   let indexes = JSON.parse(img.getAttribute('indexes'));
        //   let src = img.getAttribute('src');

        //   let allLeftChild = Array.from(this.$refs['left-container'].children).slice(indexes['startIndex'] + 1, indexes['endIndex'] + 1);
        //   let allTaskRow = Array.from(this.$refs['task-container'].children).slice(indexes['startIndex'], indexes['endIndex']);

        //   if (src == 'https://dsmlib.logistics.systems/Icons/icons8-add-new-50.png') { // To Open
        //     img.setAttribute('src', 'https://dsmlib.logistics.systems/Icons/icons8-reduce-50.png');
        //     allLeftChild.forEach(l => l.style.display = 'flex');
        //     allTaskRow.forEach(l => l.style.display = 'flex');
        //   } else { // To Close
        //     img.setAttribute('src', 'https://dsmlib.logistics.systems/Icons/icons8-add-new-50.png');
        //     allLeftChild.forEach(l => l.style.display = 'none');
        //     allTaskRow.forEach(l => l.style.display = 'none');
        //   }

        //   this.changeVerticalLineHeight();
        // });
        // taskLeftColumns.appendChild(closeImg);
        // For close child button

        if (this.showCategoryRows) {
          let parentLeftColumn = null;
          if (!preserveLeftColumns) {
            parentLeftColumn = this.createLeftColumnText(colTitles, false, pstart, [leftColumn]);
            parentLeftColumn.classList.add('category-row');
            parentLeftColumn.style.height = 'auto';
            parentLeftColumn.style.backgroundColor = '#f3f5f7';
            parentLeftColumn.style.borderBottom = '1px solid #cbd5e1';
            parentLeftColumn.style.boxSizing = 'border-box';
            this.$refs['left-container'].appendChild(parentLeftColumn);
          }
          // Left side column (Parent)

          // for parent task
          let taskRow = this.createDiv(null, ['task-row'], null);
          taskRow.classList.add('category-row');
          taskRow.style.height = 'auto';
          taskRow.style.backgroundColor = '#f3f5f7';
          taskRow.style.borderBottom = '1px solid #cbd5e1';
          taskRow.style.boxSizing = 'border-box';
          if (preserveLeftColumns && preservedLeftRows[preservedRowIndex]) {
            taskRow.style.height = `${preservedLeftRows[preservedRowIndex].getBoundingClientRect().height}px`;
          }
          this.$refs['task-container'].appendChild(taskRow);
          if (!preserveLeftColumns) {
            rowHeightGroups.push({ leftRow: parentLeftColumn, timelineRow: taskRow, minimumHeight: 22 });
          }
          preservedRowIndex++;
          // for parent task
        }

        childGroups.forEach((childGroup) => {
          const u = childGroup.key;
          const childTask = childGroup.items;
          const rowSelectionKey = childTask[0]['_rowSelectionKey'] || `${p}:${u}`;
          let childTimeLine = childTask.map((ci) => {
            let start = new Date(ci['cbstart']).getTime();
            return { start: new Date(start), end: new Date(getTaskEndTimestamp(ci)) };
          });
          let cstart = Math.min.apply(
            null,
            childTimeLine.map((t) => t['start']),
          );

          // assign lanes
          const LOCATION_LABEL_H = 10;
          const LANE_H = 25 + LOCATION_LABEL_H;
          const sorted = [...childTask].sort((a, b) => new Date(a['cbstart']) - new Date(b['cbstart']));
          const lanes = [];
          sorted.forEach((ct) => {
            const s = new Date(ct['cbstart']).getTime();
            const e = getTaskEndTimestamp(ct);
            let placed = false;
            for (let li = 0; li < lanes.length; li++) {
              if (lanes[li] <= s) {
                ct._lane = li;
                lanes[li] = e;
                placed = true;
                break;
              }
            }
            if (!placed) {
              ct._lane = lanes.length;
              lanes.push(e);
            }
          });
          const numLanes = lanes.length;
          const rowH = numLanes * LANE_H;

          const rowBg = rowIndex % 2 === 0 ? 'rgba(226, 228, 255, 0.24)' : 'rgba(255, 255, 255, 0.4)';
          rowIndex++;

          // left column
          let leftCol = null;
          if (!preserveLeftColumns) {
            leftCol = this.createLeftColumnText(colTitles, true, cstart, childTask);
            leftCol.style.height = rowH + 'px';
            leftCol.style.backgroundColor = rowBg;
            leftCol.setAttribute('data-row-selection-key', rowSelectionKey);
            leftCol.classList.toggle('task-row-selected', rowSelectionKey === this.selectedRowKey);
            this.$refs['left-container'].appendChild(leftCol);
          }

          // task row
          const childTaskRow = this.createDiv(null, ['task-row'], null);
          childTaskRow.style.height =
            preserveLeftColumns && preservedLeftRows[preservedRowIndex] ?
              `${preservedLeftRows[preservedRowIndex].getBoundingClientRect().height}px`
            : rowH + 'px';
          childTaskRow.style.backgroundColor = rowBg;
          childTaskRow.setAttribute('child', u);
          childTaskRow.setAttribute('parent', p);
          childTaskRow.setAttribute('data-row-selection-key', rowSelectionKey);
          childTaskRow.classList.toggle('task-row-selected', rowSelectionKey === this.selectedRowKey);
          childTask.forEach((ct) => {
            const ctStart = new Date(ct['cbstart']).getTime();
            const ctEnd = getTaskEndTimestamp(ct);
            const taskEl = this.createTask(ctStart, ctEnd, ct);
            taskEl.style.top = ct._lane * LANE_H + LOCATION_LABEL_H + 1 + 'px';
            childTaskRow.appendChild(taskEl);
          });
          this.$refs['task-container'].appendChild(childTaskRow);
          if (!preserveLeftColumns) {
            rowHeightGroups.push({ leftRow: leftCol, timelineRow: childTaskRow, minimumHeight: rowH });
          }
          preservedRowIndex++;
        });
      });

      if (!preserveLeftColumns) {
        this.syncGeneratedRowHeights(rowHeightGroups);
      }
      this.changeVerticalLineHeight();

      this.$nextTick(() => {
        const now = new Date();
        if (
          this.showNowMarker &&
          !this.hasAutoScrolledToNow &&
          now.getTime() >= this.start &&
          now.getTime() <= this.end
        ) {
          // Auto scroll the gantt chart to show the now marker in the middle
          this.scrollToTime(now, true);
          this.hasAutoScrolledToNow = true;
        }
      });
    },
    scrollToTime: function (date, center) {
      let scrollableGantt = this.$refs['ganttMain'];

      let scroll = ((date - this.start) / 1000 / 60 / this.effectiveInterval) * this.timeWidth;

      let ganttWidth = scrollableGantt.getBoundingClientRect().width - this.leftContainerWidth;
      if (center) {
        ganttWidth /= 2;
        scroll -= ganttWidth;
      } else {
        scroll -= 25;
      }

      scrollableGantt.scrollTo({ behavior: 'smooth', left: scroll, top: scrollableGantt.scrollTop });
    },
    getTimelineViewportWidth: function () {
      const scrollableGantt = this.$refs['ganttMain'];
      if (!scrollableGantt) {
        return 0;
      }
      return Math.max(0, scrollableGantt.clientWidth - this.leftContainerWidth);
    },
    captureViewportState: function (intervalOverride, timeWidthOverride) {
      const scrollableGantt = this.$refs['ganttMain'];
      const sourceInterval = Number(intervalOverride);
      const sourceTimeWidth = Number(timeWidthOverride);
      if (
        !scrollableGantt ||
        !isFinite(sourceInterval) ||
        sourceInterval <= 0 ||
        !isFinite(sourceTimeWidth) ||
        sourceTimeWidth <= 0
      ) {
        return null;
      }

      const timelineViewportWidth = this.getTimelineViewportWidth();
      return {
        centerTime:
          Number(this.start) +
          ((scrollableGantt.scrollLeft + timelineViewportWidth / 2) / sourceTimeWidth) * sourceInterval * 60 * 1000,
        scrollTop: scrollableGantt.scrollTop,
      };
    },
    getViewportCenterTime: function (intervalOverride, timeWidthOverride) {
      const viewportState = this.captureViewportState(
        intervalOverride == null ? this.effectiveInterval : intervalOverride,
        timeWidthOverride == null ? this.timeWidth : timeWidthOverride,
      );
      return viewportState ? viewportState.centerTime : null;
    },
    restoreViewportState: function (viewportState) {
      const scrollableGantt = this.$refs['ganttMain'];
      if (
        !scrollableGantt ||
        !viewportState ||
        viewportState.centerTime == null ||
        !this.timeWidth ||
        !this.effectiveInterval
      ) {
        return;
      }

      const timelineViewportWidth = this.getTimelineViewportWidth();
      const requestedScrollLeft =
        ((viewportState.centerTime - Number(this.start)) / this.effectiveInterval / 60 / 1000) * this.timeWidth -
        timelineViewportWidth / 2;
      const maxScrollLeft = Math.max(0, scrollableGantt.scrollWidth - scrollableGantt.clientWidth);
      const maxScrollTop = Math.max(0, scrollableGantt.scrollHeight - scrollableGantt.clientHeight);

      scrollableGantt.scrollLeft = Math.min(maxScrollLeft, Math.max(0, requestedScrollLeft));
      scrollableGantt.scrollTop = Math.min(maxScrollTop, Math.max(0, viewportState.scrollTop || 0));
      this.updateGoToNowVisibility();
    },
    restoreViewportCenterTime: function (time) {
      this.restoreViewportState({ centerTime: time, scrollTop: this.$refs['ganttMain']?.scrollTop || 0 });
    },
    queueViewportStateRestore: function (viewportState) {
      if (!viewportState) {
        return;
      }
      if (this.viewportRestoreFrame) {
        window.cancelAnimationFrame(this.viewportRestoreFrame);
        this.viewportRestoreFrame = 0;
      }

      this.$nextTick(() => {
        this.viewportRestoreFrame = window.requestAnimationFrame(() => {
          this.viewportRestoreFrame = 0;
          this.restoreViewportState(viewportState);
        });
      });
    },
    zoomLevelIndex: function () {
      const index = ganttZoomLevels.findIndex((level) => level.interval === this.effectiveInterval);
      return index >= 0 ? index : 0;
    },
    canZoomOut: function () {
      return this.zoomLevelIndex() > 0;
    },
    canZoomIn: function () {
      return this.zoomLevelIndex() < ganttZoomLevels.length - 1;
    },
    zoomOutLabel: function () {
      return ganttZoomLevels[Math.max(0, this.zoomLevelIndex() - 1)].label;
    },
    zoomInLabel: function () {
      return ganttZoomLevels[Math.min(ganttZoomLevels.length - 1, this.zoomLevelIndex() + 1)].label;
    },
    zoomOut: function () {
      if (this.canZoomOut()) {
        this.$emit('intervalChanged', ganttZoomLevels[this.zoomLevelIndex() - 1].interval);
      }
    },
    zoomIn: function () {
      if (this.canZoomIn()) {
        this.$emit('intervalChanged', ganttZoomLevels[this.zoomLevelIndex() + 1].interval);
      }
    },
    finishGoToNowNavigation: function () {
      if (this.goToNowScrollEndTimer) {
        window.clearTimeout(this.goToNowScrollEndTimer);
        this.goToNowScrollEndTimer = 0;
      }

      this.isNavigatingToNow = false;
      this.$nextTick(() => this.updateGoToNowVisibility());
    },
    scheduleGoToNowNavigationEnd: function (delay) {
      if (this.goToNowScrollEndTimer) {
        window.clearTimeout(this.goToNowScrollEndTimer);
      }

      this.goToNowScrollEndTimer = window.setTimeout(() => {
        this.finishGoToNowNavigation();
      }, delay);
    },
    goToNow: function (event) {
      this.isNavigatingToNow = true;
      this.showGoToNow = false;

      if (event && event.currentTarget) {
        event.currentTarget.blur();
      }

      this.scrollToTime(new Date(), true);

      // Fallback for browsers that do not emit scroll events for a no-op or
      // interrupted smooth scroll. Normal scroll events shorten this delay.
      this.scheduleGoToNowNavigationEnd(700);
    },
    scrolling: function () {
      if (this.isNavigatingToNow) {
        // Do not let intermediate smooth-scroll positions make the button
        // reappear. Recalculate only after scrolling has settled.
        this.showGoToNow = false;
        this.scheduleGoToNowNavigationEnd(120);
        return;
      }

      this.updateGoToNowVisibility();
    },
    updateGoToNowVisibility: function () {
      if (this.isNavigatingToNow) {
        this.showGoToNow = false;
        return;
      }

      const scrollableGantt = this.$refs['ganttMain'];
      const nowMarker = this.$refs['now-marker'];
      if (!this.showNowMarker || !scrollableGantt || !nowMarker) {
        this.showGoToNow = false;
        return;
      }

      const markerBounds = nowMarker.getBoundingClientRect();
      const timelineBounds = scrollableGantt.getBoundingClientRect();
      const ganttBounds = this.$el.getBoundingClientRect();
      const leftBound = timelineBounds.left + this.leftContainerWidth;
      const rightBound = timelineBounds.right;
      if (markerBounds.right <= leftBound) {
        this.toNowButtonStyling['left'] = `${leftBound - ganttBounds.left + 3}px`;
        this.toNowButtonStyling['justifyContent'] = 'flex-start';
        this.showGoToNow = true;
      } else if (markerBounds.left >= rightBound) {
        const zoomControlsWidth = this.showZoomControls ? 52 : 0;
        this.toNowButtonStyling['left'] =
          `${Math.max(leftBound, rightBound - 103 - zoomControlsWidth) - ganttBounds.left}px`;
        this.toNowButtonStyling['justifyContent'] = 'flex-end';
        this.showGoToNow = true;
      } else {
        this.showGoToNow = false;
      }
    },
    placeNowMarker: function () {
      let nowTime = new Date().getTime();
      if (nowTime <= this.end && nowTime >= this.start) {
        let minDiff = Math.floor((nowTime - this.start) / 1000 / 60);
        if (this.$refs['now-marker']) {
          this.$refs['now-marker'].style.left =
            `${(minDiff / this.effectiveInterval) * this.timeWidth + this.leftContainerWidth}px`;
          this.$nextTick(() => this.updateGoToNowVisibility());
        }
      } else {
        if (this.nowMarkerInterval) {
          window.clearInterval(this.nowMarkerInterval);
        }
        this.showNowMarker = false;
        this.showGoToNow = false;
      }
    },
    updateSelectedTask: function () {
      if (!this.$refs['task-container']) {
        return;
      }
      Array.from(this.$refs['task-container'].getElementsByClassName('gantt-task-block')).forEach((task) => {
        const isSelected = !!this.selectedTaskKey && task.getAttribute('data-selection-key') === this.selectedTaskKey;
        task.classList.toggle('gantt-task-selected', isSelected);
        if (task.hasAttribute('aria-pressed')) {
          task.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        }
      });
    },
    updateSelectedRow: function () {
      const selectedRowKey = this.selectedRowKey;
      [this.$refs['left-container'], this.$refs['task-container']].forEach((container) => {
        if (!container) {
          return;
        }
        Array.from(container.getElementsByClassName('task-row')).forEach((row) => {
          const isSelected = !!selectedRowKey && row.getAttribute('data-row-selection-key') === selectedRowKey;
          row.classList.toggle('task-row-selected', isSelected);
        });
      });
    },
    taskElementFromTarget: function (target) {
      const taskContainer = this.$refs['task-container'];
      let element = target;
      while (element && element !== taskContainer) {
        if (element.classList && element.classList.contains('gantt-task-block')) {
          return element;
        }
        element = element.parentNode;
      }
      return null;
    },
    showTaskTooltip: function (taskItem, event) {
      if (!this._tooltip || !taskItem) {
        return;
      }
      const fromText = taskItem['cbltext'] || '';
      this._tooltip.innerHTML = `
        <div class="tt-title">${taskItem['cbtext'] || ''}</div>
        ${taskItem['_source'] ? `<div class="tt-row"><span>Source: </span><span>${this.sourceLabel(taskItem['_source'])}</span></div>` : ''}
        ${taskItem['_conflictLabel'] ? `<div class="tt-row"><span>Conflict: </span><span>${taskItem['_conflictLabel']}</span></div>` : ''}
        <div class="tt-row"><span>Starts at: </span><span>${new Date(taskItem['cbstart']).formatString('yyyy-MM-dd HH:mm')}</span></div>
        <div class="tt-row"><span>Ends at: </span><span>${new Date(getTaskEndTimestamp(taskItem)).formatString('yyyy-MM-dd HH:mm')}</span></div>
        ${fromText ? `<div class="tt-row"><span>From: </span><span>${fromText.split(' ')[1] || fromText}</span></div>` : ''}
        ${taskItem['cbrtext'] ? `<div class="tt-row"><span>To: </span><span>${taskItem['cbrtext']}</span></div>` : ''}
        ${fromText ? `<div class="tt-row"><span>Movement : ${fromText.split(' ')[0]}</span></div>` : ''}
      `;
      this._tooltip.style.display = 'block';
      this._tooltip.style.left = event.clientX + 12 + 'px';
      this._tooltip.style.top = event.clientY - 10 + 'px';
    },
    bindTaskContainerEvents: function () {
      const taskContainer = this.$refs['task-container'];
      if (!taskContainer || this.taskContainerHandlers) {
        return;
      }
      const selectTask = (event) => {
        const taskElement = this.taskElementFromTarget(event.target);
        const taskItem = taskElement && taskElement._ganttTaskItem;
        if (!taskItem) {
          return;
        }
        event.stopPropagation();
        this.$emit('taskClicked', taskItem);
      };
      const moveTooltip = (event) => {
        const taskElement = this.taskElementFromTarget(event.target);
        if (taskElement && taskElement._ganttTaskItem && this._tooltip) {
          this._tooltip.style.left = event.clientX + 12 + 'px';
          this._tooltip.style.top = event.clientY - 10 + 'px';
        }
      };
      const showTooltip = (event) => {
        const taskElement = this.taskElementFromTarget(event.target);
        if (
          !taskElement ||
          !taskElement._ganttTaskItem ||
          this.taskElementFromTarget(event.relatedTarget) === taskElement
        ) {
          return;
        }
        this.showTaskTooltip(taskElement._ganttTaskItem, event);
      };
      const hideTooltip = (event) => {
        const taskElement = this.taskElementFromTarget(event.target);
        if (!taskElement || this.taskElementFromTarget(event.relatedTarget) === taskElement || !this._tooltip) {
          return;
        }
        this._tooltip.style.display = 'none';
      };
      const doubleClickTask = (event) => {
        const taskElement = this.taskElementFromTarget(event.target);
        const taskItem = taskElement && taskElement._ganttTaskItem;
        if (!taskItem) {
          return;
        }
        event.stopPropagation();
        this.$emit('taskDoubleClicked', taskItem);
      };
      const keydownTask = (event) => {
        const taskElement = this.taskElementFromTarget(event.target);
        const taskItem = taskElement && taskElement._ganttTaskItem;
        if (!taskItem || (event.key !== 'Enter' && event.key !== ' ')) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.$emit('taskClicked', taskItem);
      };
      this.taskContainerHandlers = {
        click: selectTask,
        dblclick: doubleClickTask,
        keydown: keydownTask,
        mouseover: showTooltip,
        mousemove: moveTooltip,
        mouseout: hideTooltip,
      };
      Object.keys(this.taskContainerHandlers).forEach((eventName) => {
        taskContainer.addEventListener(eventName, this.taskContainerHandlers[eventName]);
      });
    },
    unbindTaskContainerEvents: function () {
      const taskContainer = this.$refs['task-container'];
      if (!taskContainer || !this.taskContainerHandlers) {
        return;
      }
      Object.keys(this.taskContainerHandlers).forEach((eventName) => {
        taskContainer.removeEventListener(eventName, this.taskContainerHandlers[eventName]);
      });
      this.taskContainerHandlers = null;
    },
    createTask: function (start, end, taskItem, isCategory) {
      const taskStart = Number(start);
      const taskEnd = Number(end);
      const rangeStart = Number(this.start);
      const rangeEnd = Number(this.end);
      const visibleStart = Math.max(taskStart, rangeStart);
      const visibleEnd = Math.min(taskEnd, rangeEnd);
      const isVisible = !isNaN(visibleStart) && !isNaN(visibleEnd) && visibleEnd > visibleStart;
      let width = isVisible ? ((visibleEnd - visibleStart) / 1000 / 60 / this.effectiveInterval) * this.timeWidth : 0;
      let startPos =
        isVisible ? ((visibleStart - rangeStart) / 1000 / 60 / this.effectiveInterval) * this.timeWidth : 0;
      if (isVisible) {
        const remainingWidth = Math.max(0, this.timeWidth * this.totalCol - startPos);
        width = Math.max(width, Math.min(8, remainingWidth));
      }

      let taskText = '';
      let taskColor = '';
      let taskLeftText = '';
      let taskRightText = '';
      const taskDisplay =
        width >= 120 ? 'wide'
        : width >= 48 ? 'compact'
        : 'marker';
      const taskAriaTitle = taskItem && taskItem['cbtext'] ? taskItem['cbtext'] : 'Scheduled event';
      if (taskItem) {
        taskText = taskItem['cbtext'] || '';
        taskColor =
          parseFloat(taskItem['cbprogress']) >= 1 ? taskItem['cbprogresscolor'] || '' : taskItem['cbcolor'] || '';
        taskLeftText = taskItem['cbltext'] || '';
        taskRightText = taskItem['cbrtext'] || '';
        const source = taskItem['_source'];
        if (source && taskDisplay !== 'marker') {
          const sourceText = taskDisplay === 'wide' ? this.sourceLabel(source) : this.sourceShortLabel(source);
          taskText = `${sourceText} · ${taskText}`;
        }
        if (taskDisplay !== 'wide') {
          taskLeftText = '';
          taskRightText = '';
        }
        if (taskDisplay === 'marker') {
          taskText = '';
        }
        if (taskItem['_conflictLabel'] && taskDisplay !== 'marker') {
          taskText = `⚠ ${taskText}`;
        }
      }

      const itemColor = taskItem ? taskItem['cbsourcecolor'] || taskColor || '#7f7f7f' : '';
      taskColor = taskColor || itemColor;
      const taskClasses = ['gantt-task-block'];
      if (isCategory) {
        taskClasses.push('gantt-task-category');
      } else {
        taskClasses.push(`gantt-task-${taskDisplay}`);
      }
      const taskSelectionKey = taskItem && !isCategory ? taskItem['_selectionKey'] || '' : '';
      const taskIsSelected = !!taskSelectionKey && taskSelectionKey === this.selectedTaskKey;
      if (taskIsSelected) {
        taskClasses.push('gantt-task-selected');
      }
      if (taskItem && taskItem['_conflictLabel']) {
        taskClasses.push('gantt-task-conflict');
      }

      const taskStyle = {
        left: `${startPos}px`,
        width: `${width}px`,
        display: isVisible ? '' : 'none',
        backgroundColor: isCategory ? '#f3f5f7' : taskColor,
        outlineColor: itemColor,
        cursor: taskItem && !isCategory ? 'pointer' : 'default',
      };
      if (isCategory) {
        taskStyle.top = '0px';
        taskStyle.height = '100%';
        taskStyle.border = '0px';
        taskStyle.boxSizing = 'border-box';
      } else if (taskItem && this.useSourceTaskBorder) {
        taskStyle.borderLeft = `4px solid ${itemColor}`;
      }

      let taskDiv = this.createDiv(null, taskClasses, taskStyle);
      taskDiv.setAttribute('data-selection-key', taskSelectionKey);
      if (isCategory) {
        return taskDiv;
      }

      if (taskItem) {
        taskDiv._ganttTaskItem = taskItem;
        taskDiv.setAttribute('role', 'button');
        taskDiv.setAttribute('tabindex', '0');
        taskDiv.setAttribute('aria-pressed', taskIsSelected ? 'true' : 'false');
        const taskAriaLabel = [
          taskAriaTitle,
          taskItem['_source'] ? this.sourceLabel(taskItem['_source']) : '',
          taskItem['ccol1text'] ? `Vehicle ${taskItem['ccol1text']}` : '',
          `Starts ${new Date(taskItem['cbstart']).formatString('yyyy-MM-dd HH:mm')}`,
          `Ends ${new Date(getTaskEndTimestamp(taskItem)).formatString('yyyy-MM-dd HH:mm')}`,
          taskItem['_conflictLabel'] ? `Conflict ${taskItem['_conflictLabel']}` : '',
        ]
          .filter((value) => value)
          .join(', ');
        taskDiv.setAttribute('aria-label', taskAriaLabel);

        taskDiv.appendChild(
          this.createDiv(
            taskLeftText,
            ['task-left-text'],
            {
              fontSize: `${taskItem['cblfontsize']}px`,
            },
            false,
          ),
        );

        taskDiv.appendChild(this.createDiv(taskRightText, ['task-right-text'], {}, false));

        taskDiv.appendChild(
          this.createDiv(null, ['task-progress'], {
            width: `${taskItem['cbprogress'] * 100}%`,
            backgroundColor: taskItem['cbprogresscolor'],
          }),
        );

        let textDiv = this.createDiv(
          taskText,
          ['task-inner-text'],
          {
            color: taskItem['cbtextcolor'],
          },
          false,
        );
        taskDiv.appendChild(textDiv);
      }

      return taskDiv;
    },
    createDiv: function (content, classList, style, showTitle) {
      let div = document.createElement('div');

      if (classList) {
        classList.forEach((c) => div.classList.add(c));
      }

      if (style) {
        Object.keys(style).forEach((s) => (div.style[s] = style[s]));
      }

      if (content) {
        div.innerHTML = content;
        if (showTitle !== false) {
          div.title = content;
        }
      }

      return div;
    },
    sourceLabel: function (source) {
      const labels = { EMS: 'Workshop Job', HMS: 'Haulage Job', TMS: 'Trucking Job', EXPIRY: 'Policy expiry' };
      const key = String(source || '')
        .trim()
        .toUpperCase();
      return labels[key] || source;
    },
    sourceShortLabel: function (source) {
      const labels = { EMS: 'WJ', HMS: 'HJ', TMS: 'TJ', EXPIRY: 'PX' };
      const key = String(source || '')
        .trim()
        .toUpperCase();
      return labels[key] || key;
    },
    createImg: function (src, classList, style) {
      let img = document.createElement('img');

      if (classList) {
        classList.forEach((c) => img.classList.add(c));
      }

      if (style) {
        Object.keys(style).forEach((s) => (img.style[s] = style[s]));
      }

      if (src) {
        img.src = src;
      }

      return img;
    },

    syncGeneratedRowHeights: function (rowHeightGroups) {
      const updates = rowHeightGroups.map((rowHeightGroup) => {
        const cells = Array.from(rowHeightGroup.leftRow.getElementsByClassName('left-column-text'));
        const rowHeight = Math.max(rowHeightGroup.minimumHeight, ...cells.map((cell) => cell.scrollHeight));
        return {
          leftRow: rowHeightGroup.leftRow,
          timelineRow: rowHeightGroup.timelineRow,
          height: rowHeight,
        };
      });

      updates.forEach((update) => {
        update.leftRow.style.height = `${update.height}px`;
        update.timelineRow.style.height = `${update.height}px`;
      });
    },
    createLeftColumnText: function (titles, isChild, rowStart, task) {
      let leftColText = this.createDiv(null, ['task-row'], null);
      if (!isChild) {
        const categoryText = titles
          .map((title) => String(task[0][`p${title}text`] || '').trim())
          .filter((value) => value)
          .join(' ');
        leftColText.appendChild(
          this.createDiv(categoryText, ['left-column-text', 'category-column-text'], {
            cursor: 'default',
            width: '100%',
            maxWidth: 'none',
            flex: '1 1 100%',
          }),
        );
        return leftColText;
      }
      titles.forEach((c, i) => {
        let style = {
          cursor: isChild ? 'pointer' : 'default',
          paddingLeft:
            isChild ?
              i == 0 ?
                '8px'
              : '5px'
            : '',
        };
        let maxWidth = this.titleMaxWidth[i];
        if (maxWidth) {
          style['maxWidth'] = maxWidth;
        }

        let colItem = null;
        const columnClasses = ['left-column-text'];
        colItem = this.createDiv(task[0][`c${c}text`], columnClasses, style);

        if (isChild) {
          colItem.addEventListener('click', () => {
            this.scrollToTime(new Date(rowStart));
            this.$emit('rowClicked', task);
          });
          colItem.addEventListener('mouseover', (e) => {
            let parent = e.target.parentNode;
            parent.classList.add('task-row-hover');

            let hoverIndex = Array.from(this.$refs['left-container'].children).indexOf(parent);
            let taskRowItem = Array.from(this.$refs['task-container'].children)[hoverIndex - 1];
            taskRowItem.classList.add('task-row-hover');
          });
          colItem.addEventListener('mouseout', (e) => {
            let parent = e.target.parentNode;
            parent.classList.remove('task-row-hover');

            let hoverIndex = Array.from(this.$refs['left-container'].children).indexOf(parent);
            let taskRowItem = Array.from(this.$refs['task-container'].children)[hoverIndex - 1];
            taskRowItem.classList.remove('task-row-hover');
          });
        }

        leftColText.appendChild(colItem);
      });
      return leftColText;
    },

    syncViewportLayout: function () {
      this.mainGantt = this.$refs['ganttMain'];
      if (!this.mainGantt || !this.$refs['left-container'] || !this.$refs['top-container']) {
        return;
      }
      const leftContainerBounds = this.$refs['left-container'].getBoundingClientRect();
      const topContainerBounds = this.$refs['top-container'].getBoundingClientRect();
      this.lastObservedTimelineWidth = Math.round(topContainerBounds.width);
      this.topLeftContainerStyling['left'] = `${leftContainerBounds.left}px`;
      this.topLeftContainerStyling['top'] = `${topContainerBounds.top}px`;
      this.topLeftContainerStyling['width'] = `${leftContainerBounds.width}px`;
      this.topLeftContainerStyling['maxWidth'] = `${leftContainerBounds.width}px`;
      this.topLeftContainerStyling['minWidth'] = `${leftContainerBounds.width}px`;
    },
    queueViewportRebuild: function () {
      this.queuedViewportState = this.captureViewportState(this.effectiveInterval, this.timeWidth);
      if (this.layoutRebuildTimer) {
        window.clearTimeout(this.layoutRebuildTimer);
      }
      this.layoutRebuildTimer = window.setTimeout(() => {
        this.layoutRebuildTimer = 0;
        if (!this.resizing && Date.now() >= this.layoutResizeSuspendedUntil) {
          const viewportState = this.queuedViewportState;
          this.queuedViewportState = null;
          this.pageResize(true, viewportState);
        }
      }, 180);
    },
    pageResize: function (preserveLeftColumns, viewportState) {
      this.syncViewportLayout();

      // create shared tooltip if it doesn't exist yet
      if (!this._tooltip) {
        this._tooltip = document.createElement('div');
        this._tooltip.className = 'gantt-hover-tooltip';
        document.body.appendChild(this._tooltip);
      }

      this.generateGantt(preserveLeftColumns);
      this.queueViewportStateRestore(viewportState);
    },
    refreshLayout: function (viewportState) {
      if (this.layoutRebuildTimer) {
        window.clearTimeout(this.layoutRebuildTimer);
        this.layoutRebuildTimer = 0;
      }
      this.pageResize(viewportState ? true : undefined, viewportState);
    },
    suspendViewportResize: function (duration) {
      this.layoutResizeSuspendedUntil = Date.now() + duration;
      if (this.layoutResizeFrame) {
        window.cancelAnimationFrame(this.layoutResizeFrame);
        this.layoutResizeFrame = 0;
      }
      if (this.layoutRebuildTimer) {
        window.clearTimeout(this.layoutRebuildTimer);
        this.layoutRebuildTimer = 0;
      }
    },

    // Called by EquipmentSchedule before the resizable right panel begins
    // changing the Gantt viewport width. The generated timeline and column
    // widths remain untouched; only the visible viewport is squeezed.
    pauseExternalLayoutResize: function () {
      this.externalLayoutResizePaused = true;

      if (this.layoutResizeFrame) {
        window.cancelAnimationFrame(this.layoutResizeFrame);
        this.layoutResizeFrame = 0;
      }
      if (this.layoutRebuildTimer) {
        window.clearTimeout(this.layoutRebuildTimer);
        this.layoutRebuildTimer = 0;
      }

      this.queuedViewportState = null;
    },

    // Do not regenerate the Gantt when the right panel finishes opening,
    // closing, or resizing. The existing fixed-width timeline is allowed to
    // overflow horizontally inside the newly narrowed viewport.
    resumeExternalLayoutResize: function () {
      this.externalLayoutResizePaused = false;

      this.$nextTick(() => {
        this.syncViewportLayout();
        this.updateGoToNowVisibility();
      });
    },

    handleTimelineViewportResize: function () {
      if (
        this.externalLayoutResizePaused ||
        this.resizing ||
        this.layoutResizeFrame ||
        Date.now() < this.layoutResizeSuspendedUntil
      ) {
        return;
      }

      this.layoutResizeFrame = window.requestAnimationFrame(() => {
        this.layoutResizeFrame = 0;
        const topContainer = this.$refs['top-container'];
        if (!topContainer) {
          return;
        }

        const width = Math.round(topContainer.getBoundingClientRect().width);
        if (!width || width === this.lastObservedTimelineWidth) {
          return;
        }

        this.lastObservedTimelineWidth = width;
        this.syncViewportLayout();
        this.queueViewportRebuild();
      });
    },
    handleWindowViewportResize: function () {
      this.handleTimelineViewportResize();
    },

    changeVerticalLineHeight: function () {
      let containerHeight = this.$refs['task-container'].getBoundingClientRect().height;

      this.$refs['left-container'].style.height = `${containerHeight + 60}px`;
      const timelineGridOverlay = this.$el.getElementsByClassName('timeline-grid-overlay')[0];
      if (timelineGridOverlay) {
        timelineGridOverlay.style.height = `${containerHeight}px`;
      }

      // Make sure now marker height reached to the bottom of the container
      this.$refs['now-marker'].style.height = `${containerHeight + 60}px`;
      // Make sure now marker height reached to the bottom of the container

      // Testing for on gantt clicked
      // this.$refs['task-container'].appendChild(this.createDiv(null, ['dot'], null));
      // Testing for on gantt clicked
    },

    handleWindowMouseUp: function () {
      if (!this.resizing) {
        return;
      }

      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = 0;
        this.applyLeftColumnResize(this.pendingResizeWidth);
      }

      this.resizing = false;
      this.leftContainerWidth = this.pendingResizeWidth;
      this.pageResize();
    },

    handleWindowMouseMove: function (e) {
      if (!this.resizing) {
        return;
      }

      this.pendingResizeWidth = Math.max(0, this.resizeStartWidth + e.pageX - this.resizeStartLeft - this.resizeOffset);

      if (this.resizeFrame) {
        return;
      }

      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = 0;
        this.applyLeftColumnResize(this.pendingResizeWidth);
      });
    },

    applyLeftColumnResize: function (width) {
      let leftContainer = this.$refs['left-container'];
      let topContainer = this.$refs['top-container'];
      let topLeftContainer = this.$refs['top-left-container'];
      let taskContainer = this.$refs['task-container'];
      let timelineGridOverlay = this.$el.getElementsByClassName('timeline-grid-overlay')[0];
      let resizer = leftContainer.getElementsByClassName('left-resizer')[0];
      if (!resizer) {
        return;
      }

      leftContainer.style.width = `${width}px`;
      leftContainer.style.minWidth = `${width}px`;
      leftContainer.style.maxWidth = `${width}px`;
      topContainer.style.width = `calc(100% - ${width}px)`;
      taskContainer.style.left = `${width}px`;
      if (timelineGridOverlay) {
        timelineGridOverlay.style.left = `${width}px`;
      }
      topLeftContainer.style.width = `${width}px`;
      topLeftContainer.style.minWidth = `${width}px`;
      topLeftContainer.style.maxWidth = `${width}px`;
      resizer.style.left = `${Math.max(0, width - 3)}px`;
      this.verticalLinePosition = width - this.resizeStartWidth;
    },

    init: function (preserveLeftColumns, viewportState) {
      if (this.items.length > 0) {
        this.items.forEach((i) => {
          const pm = (i.pcol1text || '').trim(); // Prime Mover column
          const driver = (i.pcol2text || '').trim(); // Driver column

          // Create a deterministic unique parent id
          i.pid = `${pm}||${driver}`;
        });

        // ensure cid is unique per container
        this.items.forEach((i) => {
          if (!i.cid) {
            i.cid = `${i.pid}||${i.jobno || ''}||${i.seqno || ''}`;
          }
        });

        this.items.forEach((i) => {
          if (i['cbduration'] < 0) {
            i['cbduration'] = 0;
          }
        });

        let itemTimeLine = this.items.map((i) => {
          let start = new Date(i['cbstart']).getTime();
          return { start: new Date(start), end: new Date(getTaskEndTimestamp(i)) };
        });
        let minStart = Math.min.apply(
          null,
          itemTimeLine.map((t) => t['start']),
        );
        let maxEnd = Math.max.apply(
          null,
          itemTimeLine.map((t) => t['end']),
        );
        this.start = new Date(minStart);
        this.end = new Date(maxEnd);
      }

      const rangeStart = this.rangeStart ? new Date(this.rangeStart) : null;
      const rangeEnd = this.rangeEnd ? new Date(this.rangeEnd) : null;
      if (rangeStart && !isNaN(rangeStart.getTime())) {
        this.start = rangeStart;
      }
      if (rangeEnd && !isNaN(rangeEnd.getTime())) {
        this.end = rangeEnd;
      }

      this.showNowMarker = true;

      if (this.effectiveInterval === DAILY_INTERVAL || this.effectiveInterval === HALF_DAY_INTERVAL) {
        // Keep every calendar-day header aligned to the requested day-based
        // interval, including the first and last day in the range.
        const originalEnd = new Date(this.end);
        this.start.setHours(0, 0, 0, 0);
        this.end.setHours(0, 0, 0, 0);
        if (originalEnd.getTime() > this.end.getTime()) {
          this.end.setDate(this.end.getDate() + 1);
        }
        if (this.end.getTime() <= this.start.getTime()) {
          this.end = new Date(this.start);
          this.end.setDate(this.end.getDate() + 1);
        }
        this.start = this.start.getTime();
        this.end = this.end.getTime();
      } else {
        this.start.setSeconds(0, 0);
        this.end.setSeconds(0, 0);
        let startDiff = (this.start.getHours() * 60 + this.start.getMinutes()) % this.effectiveInterval;
        this.start = this.start.setMinutes(this.start.getMinutes() - startDiff);

        let endDiff = (this.end.getHours() * 60 + this.end.getMinutes()) % this.effectiveInterval;
        if (endDiff > 0) {
          this.end = this.end.setMinutes(this.end.getMinutes() + (this.effectiveInterval - endDiff));
        } else {
          this.end = this.end.getTime();
        }
      }

      this.totalCol = Math.max(1, Math.ceil((this.end - this.start) / 1000 / 60 / this.effectiveInterval)); // in minutes

      this.$nextTick(() => this.pageResize(preserveLeftColumns, viewportState));
    },
  },
  mounted() {
    this.bindTaskContainerEvents();
    if (Number(this.interval) > DAILY_INTERVAL) {
      this.$emit('intervalChanged', DAILY_INTERVAL);
    }
    this.init();

    window.addEventListener('resize', this.handleWindowViewportResize);
    window.addEventListener('mouseup', this.handleWindowMouseUp);
    window.addEventListener('mousemove', this.handleWindowMouseMove);

    // Deliberately do not observe top-container width changes.
    // Opening or resizing the right panel should squeeze the viewport and
    // expose horizontal overflow without recalculating timeline columns.
    // Real browser-window resizing is still handled by the window listener.
  },
  beforeDestroy() {
    this.unbindTaskContainerEvents();
    window.removeEventListener('resize', this.handleWindowViewportResize);
    window.removeEventListener('mouseup', this.handleWindowMouseUp);
    window.removeEventListener('mousemove', this.handleWindowMouseMove);

    if (this.resizeFrame) {
      window.cancelAnimationFrame(this.resizeFrame);
    }
    if (this.layoutResizeFrame) {
      window.cancelAnimationFrame(this.layoutResizeFrame);
    }
    if (this.viewportRestoreFrame) {
      window.cancelAnimationFrame(this.viewportRestoreFrame);
    }
    if (this.layoutRebuildTimer) {
      window.clearTimeout(this.layoutRebuildTimer);
    }
    if (this.goToNowScrollEndTimer) {
      window.clearTimeout(this.goToNowScrollEndTimer);
      this.goToNowScrollEndTimer = 0;
    }
    if (this.layoutResizeObserver) {
      this.layoutResizeObserver.disconnect();
      this.layoutResizeObserver = null;
    }

    if (this._tooltip) {
      document.body.removeChild(this._tooltip);
      this._tooltip = null;
    }
  },
  watch: {
    items: function (val) {
      if (val.length == 0 && !this.rangeStart && !this.rangeEnd) {
        this.start = new Date(new Date().getTime() - 60 * 60 * 1000);
        this.end = new Date(new Date().getTime() + 60 * 60 * 1000);
      }
      this.init();
    },

    rangeStart: function () {
      this.init();
    },

    rangeEnd: function () {
      this.init();
    },

    interval: function (newInterval, oldInterval) {
      // The watcher runs after `interval` has already changed. Capture the
      // old pixel-to-time mapping explicitly so the same time stays centred.
      const previousInterval = Math.min(DAILY_INTERVAL, Math.max(1, Number(oldInterval) || 30));
      const viewportState = this.captureViewportState(previousInterval, this.timeWidth);

      if (Number(newInterval) > DAILY_INTERVAL) {
        this.$emit('intervalChanged', DAILY_INTERVAL);
      }

      this.init(true, viewportState);
    },

    selectedTaskKey: function () {
      this.$nextTick(() => this.updateSelectedTask());
    },

    selectedRowKey: function () {
      this.$nextTick(() => this.updateSelectedRow());
    },

    showCategoryRows: function () {
      this.init();
    },

    timeZoneWidth: function () {
      this.$nextTick(() => this.updateGoToNowVisibility());
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
$ganttColor: rgba(128, 128, 128, 0.3);
$innerLines: rgba(200, 200, 200, 0.5);

* {
  box-sizing: border-box;
}

.gantt-main {
  height: 100%;
  width: 100%;
  min-height: 0;
  min-width: 0;
  position: relative;
  overflow: hidden;
}

.gantt-scroll-container {
  height: 100%;
  width: 100%;
  border: 1px solid $ganttColor;
  display: flex;
  position: relative;
  // Reserve the vertical scrollbar from the first empty render at every
  // zoom level. Data arriving later therefore does not alter the data width.
  overflow-x: auto;
  overflow-y: scroll;
  scrollbar-gutter: stable;

  // Do not style ::-webkit-scrollbar here. Chrome/Electron will use
  // its normal native scrollbar appearance for the current OS.

  > .left-container,
  > .top-container {
    position: sticky;
    flex: 0 0 auto;
  }

  > .main-container {
    // background-color: red;
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
    padding-left: 200px;
    padding-top: 60px;

    > .inner {
      width: 50px;
      height: 1000px;
      background-color: green;
    }

    > .inner2 {
      width: 1500px;
      height: 50px;
      background-color: green;
    }
  }

  > .left-container {
    left: 0;
    background-color: white;
    min-width: 200px;
    min-height: 100%;
    border-right: 1px solid $ganttColor;
    z-index: 4;
    padding-top: 60px;
  }

  > .top-container {
    background-color: white;
    height: 60px;
    top: 0;
    z-index: 3;
    font-size: 10px;
    // min-width: calc(100% - 200px);

    > .top-row {
      height: 20px;
      border-bottom: 1px solid $ganttColor;
      display: flex;
      align-items: center;
      min-width: 100%;
      width: fit-content;
      background-color: white;
    }
  }

  > .top-left-container {
    z-index: 5;
    height: 60px;
    // min-width: 199px;
    background-color: white;
    position: fixed;
    left: 0;
    top: 0;
    border-bottom: 1px solid $ganttColor;
    border-right: 1px solid $ganttColor;
    display: flex;
  }

  > .task-container {
    position: absolute;
    top: 60px;
    min-height: calc(100% - 60px);
    z-index: 1;
  }
}

.gantt-main > .to-now-button {
  position: absolute;
  z-index: 6;
  top: 0;
  display: flex;
  align-items: center;
  width: 100px;
  height: 20px;
  padding: 0 5px;
  border: 0 !important;
  outline: 0 !important;
  box-shadow: none !important;
  background: transparent !important;
  color: inherit !important;
  font-size: 10px;
  cursor: pointer;

  > .mid-text {
    text-decoration: underline;
  }

  > span {
    margin: 0 3px;
  }
}

.gantt-main > .gantt-zoom-controls {
  position: absolute;
  z-index: 6;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: 2px;
  height: 20px;
  border: 1px solid $innerLines;
  border-bottom: 0;
  background-color: white;
  font-size: 10px;

  > button {
    width: 22px;
    height: 20px;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
    font-size: 14px;
    line-height: 16px;

    &:hover:not(:disabled) {
      background-color: rgba(79, 129, 189, 0.12);
    }

    &:disabled {
      color: #a0a7af;
      cursor: default;
    }
  }
}

.now-marker {
  height: 100%;
  width: 2px;
  background-color: rgba(255, 0, 0, 0.5);
  position: absolute;
  left: 400px;
  top: 0;
  z-index: 2;
}

.now-marker-text {
  background-color: rgba(255, 0, 0, 0.5);
  position: relative;
  top: 60px;
  width: fit-content;
  padding: 3px 10px;
  color: white;
  font-size: 12px;
}
</style>

<style lang="scss">
$ganttColor: rgba(0, 0, 0, 0.3);
$innerLines: rgba(200, 200, 200, 0.5);
$columnLines: #eef0f2;

.left-resizer {
  height: calc(100% - 60px);
  width: 6px;
  cursor: col-resize;
  position: absolute;
  left: calc(100% - 3px);
  z-index: 10;
}

.row-content {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  white-space: nowrap;
  border-right: 1px solid $innerLines;
  height: 100%;
}

.today-row-date {
  background-color: rgba(0, 255, 255, 0.3) !important;
}

.alternate-row-date {
  background-color: rgba(226, 228, 255, 0.18);
}

.gantt-scroll-container > .timeline-grid-overlay {
  position: absolute;
  display: grid;
  pointer-events: none;
  overflow: hidden;
  z-index: 0;

  > .timeline-grid-column {
    min-width: 0;
    border-right: 1px solid var(--gantt-grid-color);
  }

  > .timeline-grid-column.hour-boundary {
    border-right-color: var(--gantt-hour-grid-color);
  }

  > .timeline-grid-column.last-column {
    border-right: 0;
  }
}

.task-row {
  height: 25px;
  // background-color: rgba(0,0,0,0.5);
  position: relative;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  border-bottom: 1px solid $innerLines;
  width: 100%;
  overflow: hidden;
}

.gantt-scroll-container > .task-container > .task-row.category-row {
  background-color: #f3f5f7 !important;
  z-index: 2;
}

.gantt-scroll-container > .left-container > .task-row.category-row {
  overflow: visible;
  z-index: 3;
}

.gantt-scroll-container > .left-container > .task-row.category-row::after {
  content: '';
  position: absolute;
  top: 0;
  right: -1px;
  bottom: 0;
  width: 1px;
  background-color: inherit;
}

.task-row-hover,
.task-row-selected {
  background-color: rgba(255, 255, 0, 0.5) !important;
}

.gantt-task-block {
  position: absolute;
  height: 20px;
  box-sizing: border-box;
  background-color: rgb(255, 225, 225);
  border: 1px solid $ganttColor;
  cursor: pointer;
  min-width: 5px;
  overflow: visible;
  z-index: 2;
}

.gantt-task-block:not(.gantt-task-category) {
  border-left: 1px solid $ganttColor;
}

.gantt-task-block.gantt-task-selected {
  z-index: 3;
  outline: 3px solid #1f6fb2;
  outline-offset: -3px;
}

.gantt-task-block.gantt-task-conflict {
  box-shadow: inset 0 2px 0 #c2410c;
}

.gantt-task-block:focus-visible,
.to-now-button:focus-visible,
.gantt-zoom-controls > button:focus-visible {
  outline: 2px solid #1f6fb2 !important;
  outline-offset: -2px;
}

.gantt-task-block.gantt-task-category {
  height: 100%;
  min-width: 0;
  top: 0;
  border: 0;
  cursor: default;
  pointer-events: none;
}

.left-column-text {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  // justify-content: center;
  font-size: 10px;
  line-height: 1.15;
  padding: 2px 4px;
  height: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  overflow: visible;
  text-overflow: clip;
  width: 50%;
  flex-grow: 1;
}

.task-left-text,
.task-right-text {
  position: absolute;
  top: -10px;
  height: 10px;
  box-sizing: border-box;
  padding: 0 1px;
  display: flex;
  align-items: center;
  line-height: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
  z-index: 3;
  background-color: transparent;
}

.task-left-text {
  left: 0;
  right: auto;
  width: 50%;
  max-width: 50%;
  font-size: 8px;
  text-align: left;
  justify-content: flex-start;
}

.task-right-text {
  left: auto;
  right: 0;
  width: 50%;
  max-width: 50%;
  font-size: 8px;
  text-align: right;
  justify-content: flex-end;
}

.task-progress {
  position: absolute;
  left: 0;
  top: auto;
  bottom: 0;
  height: 18px;
  background-color: red;
  z-index: 1;
}

.task-inner-text {
  position: absolute;
  left: 0;
  right: 0;
  top: auto;
  bottom: 0;
  height: 18px;
  width: 100%;
  box-sizing: border-box;
  padding: 0 4px;
  overflow: hidden;
  white-space: nowrap;
  display: flex;
  align-items: center;
  text-align: center;
  font-size: 11px;
  z-index: 2;
}

.category-column-text {
  flex: 1 1 100%;
  width: 100%;
  height: auto;
  min-height: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  text-overflow: clip;
  line-height: 1.1;
  padding: 2px 4px;
  color: #334155;
  font-weight: 600;
  border-right: 0;
}

.category-column-text:last-child {
  border-right: 0;
}

.dot {
  background-color: red;
  border-radius: 100%;
  height: 10px;
  width: 10px;
  position: absolute;
  z-index: 99;
}

.gantt-hover-tooltip {
  display: none;
  position: fixed;
  z-index: 9999;
  background: rgba(50, 50, 50, 0.95);
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 11px;
  pointer-events: none;
  min-width: 180px;
  max-width: 280px;
}
.gantt-hover-tooltip .tt-title {
  font-weight: 600;
  font-size: 12px;
  margin-bottom: 6px;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.gantt-hover-tooltip .tt-row {
  display: flex;
  gap: 4px;
  color: #cccccc;
  margin-bottom: 3px;
  font-size: 11px;
}
.gantt-hover-tooltip .tt-row span:first-child {
  white-space: nowrap;
}
</style>
